/* eslint-disable @typescript-eslint/no-explicit-any */

import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { PasswordInput } from '@/components/PasswordInput'
import { useMeQuery } from '@/hooks/useAuth'
import { useChangePasswordMutation } from '@/hooks/useAuth'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import Loading from '@/components/Loading'
import { useUpdateUserMutation } from '@/hooks/useUsers'
import DigestSettingsCard from '@/components/settings/DigestSettingsCard'
import PlanInfoCard from '@/components/plan/PlanInfoCard'
import EntitlementsCard from '@/components/plan/EntitlementsCard'
import DunningRulesCard from '@/components/dunning/DunningRulesCard'
import { useEffect } from 'react'
import OwnerBillingOverviewCard from '@/components/billing/OwnerBillingOverviewCard'
import { MessagePacksCard } from '@/components/billing/MessagePacksCard'
import OwnerInvoiceSettingsCard from '@/components/settings/OwnerInvoiceSettingsCard'

const infoSchema = z.object({
  name: z.string().min(2, 'Nom trop court').optional().or(z.literal('')),
  email: z.email('Email invalide').optional().or(z.literal('')),
})
type InfoValues = z.infer<typeof infoSchema>

const pwdSchema = z
  .object({
    currentPassword: z.string().min(6, 'Min 6 caractères'),
    newPassword: z.string().min(8, 'Min 8 caractères'),
    confirm: z.string().min(8, 'Min 8 caractères'),
  })
  .refine((v) => v.newPassword === v.confirm, {
    path: ['confirm'],
    message: 'Les mots de passe ne correspondent pas',
  })
type PwdValues = z.infer<typeof pwdSchema>

export default function ProfilePage() {
  const { data, status } = useMeQuery(true)
  const user = data?.user
  const userId = user?.id
  const updateMut = useUpdateUserMutation(userId ?? 'noop')
  const changePwdMut = useChangePasswordMutation()

  const fInfo = useForm<InfoValues>({
    resolver: zodResolver(infoSchema),
    values: {
      name: data?.user.name ?? '',
      email: data?.user.email ?? '',
    },
  })

  useEffect(() => {
    if (!user) return
    fInfo.reset({
      name: user.name ?? '',
      email: user.email ?? '',
    })
  }, [fInfo, user, userId])

  const fPwd = useForm<PwdValues>({
    resolver: zodResolver(pwdSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
  })

  if (status === 'pending') return <Loading />
  if (!user)
    return <div className='text-sm text-muted-foreground'>Non connecté</div>

  const saveInfo = async (values: InfoValues) => {
    if (!userId) return

    const payload: any = {}
    const name = values.name?.trim() || ''
    const email = values.email?.trim().toLowerCase() || ''

    if (name && name !== data?.user.name) payload.name = name
    if (email && email !== data?.user.email) payload.email = email

    if (!payload.name && !payload.email) {
      toast.info('Aucun changement')
      return
    }

    try {
      await updateMut.mutateAsync(payload)
      toast.success('Profil mis à jour')
    } catch (e: any) {
      const msg = e?.response?.data?.message
      toast.error('Mise à jour impossible', {
        description: msg ?? 'Erreur inconnue',
      })
    }
  }

  const changePassword = async (values: PwdValues) => {
    try {
      await changePwdMut.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
      toast.success('Mot de passe modifié')
      fPwd.reset()
    } catch (e: any) {
      toast.error('Échec de la modification', {
        description:
          e?.response?.data?.message ?? 'Vérifiez le mot de passe actuel',
      })
    }
  }

  return (
    <div className='grid gap-6 md:grid-cols-2'>
      {/* Infos personnelles */}
      <Card>
        <CardHeader>
          <h2 className='text-xl font-semibold'>Informations personnelles</h2>
          <p className='text-sm text-muted-foreground'>
            Mettez à jour votre nom et votre email.
          </p>
        </CardHeader>
        <CardContent>
          <Form {...fInfo}>
            <form onSubmit={fInfo.handleSubmit(saveInfo)} className='space-y-4'>
              <FormField
                control={fInfo.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom</FormLabel>
                    <FormControl>
                      <Input placeholder='Votre nom' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={fInfo.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type='email'
                        placeholder='Votre email'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='flex items-center justify-end gap-2 pt-2'>
                <Button type='submit' disabled={updateMut.isPending}>
                  {updateMut.isPending ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
        <CardFooter className='text-xs text-muted-foreground'>
          Rôle : <b className='mx-1'>{data?.user.role}</b> — ID propriétaire :{' '}
          <b className='mx-1'>{data?.user.ownerId}</b>
        </CardFooter>
      </Card>

      {/* Sécurité */}
      <Card>
        <CardHeader>
          <h2 className='text-xl font-semibold'>Sécurité</h2>
          <p className='text-sm text-muted-foreground'>
            Modifiez votre mot de passe.
          </p>
        </CardHeader>
        <CardContent>
          <Form {...fPwd}>
            <form
              onSubmit={fPwd.handleSubmit(changePassword)}
              className='space-y-4'
            >
              <FormField
                control={fPwd.control}
                name='currentPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mot de passe actuel</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder='••••••' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={fPwd.control}
                name='newPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nouveau mot de passe</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder='Min 8 caractères'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={fPwd.control}
                name='confirm'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmer</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder='Répétez le mot de passe'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='flex items-center justify-end gap-2 pt-2'>
                <Button type='submit' disabled={changePwdMut.isPending}>
                  {changePwdMut.isPending
                    ? 'Mise à jour…'
                    : 'Changer le mot de passe'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      <div className='md:col-span-2'>
        <PlanInfoCard />
      </div>
      <div className='md:col-span-2'>
        <OwnerBillingOverviewCard />
      </div>
      <div className='md:col-span-2'>
        <MessagePacksCard />
      </div>

      {/* Digest settings */}
      <div className='md:col-span-2'>
        <DigestSettingsCard />
      </div>
      <div className='md:col-span-2'>
        <OwnerInvoiceSettingsCard />
      </div>
      <div className='md:col-span-2'>
        <EntitlementsCard />
      </div>
      <div className='md:col-span-2'>
        <DunningRulesCard />
      </div>
    </div>
  )
}
